import { Tooltip } from 'antd'
import { useI18n } from 'context/I18nProvider'
import type { InitialsResponse } from 'hooks/initials'
import * as React from 'react'
import type {
  AriaOnChangeProps,
  AriaOnFocusProps,
  AriaLiveMessages,
  ControlProps,
  FormatOptionLabelMeta,
  GroupBase,
  InputProps,
  OptionProps,
} from 'react-select'
import { components } from 'react-select'
import { selectModalProps } from 'utils/select'

export interface Value {
  label: string
  value: string | number
  draft?: boolean
}
interface CustomProps {
  selectProps: {
    noBorder?: boolean
    error?: boolean
    warning?: boolean
    helper?: string
    tooltip?: boolean
    inputId?: string | number
  }
}

interface TrainingControlProps {
  children: React.ReactElement
}

export interface BaseSelectProps {
  component?: React.ElementType
  id?: number | string
  name?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  ariaLiveMessages?: AriaLiveMessages<Value, boolean, GroupBase<Value>>
  className?: string
  url?: string
  label?: string
  placeholder?: string
  helper?: string | React.ReactElement
  styles?:
    | {
        option: (provided: any, state: any) => any
      }
    | {
        [key: string]: (styles: any) => any // utils/select
      }
  error?: boolean
  warning?: boolean
  insideModal?: boolean
  outline?: boolean
  noBorder?: boolean
  required?: boolean
  isMulti?: boolean
  isDisabled?: boolean
  isClearable?: boolean
  isOptionDisabled?: (option: any) => boolean
  apiPlatform?: boolean
  getOptionValue?: Function
  onChange?: Function
  mapper?: Function
  loadingMessage?: Function
  noOptionsMessage?: Function
  options?: Value[]
  defaultValue?: Value | Value[] | string | number | null
  value?: any
  ref?: any
  proxy?: (url: string, output: InitialsResponse) => InitialsResponse
  formatOptionLabel?: (option: any, labelMeta: FormatOptionLabelMeta<any>) => React.ReactNode
}

const Control = ({ children, ...props }: ControlProps & TrainingControlProps & CustomProps) => {
  const labelCss =
    props.isFocused || props.hasValue
      ? '-top-[1.2rem] text-sm px-2 bg-white'
      : 'top-[.2rem] text-base px-0 bg-transparent'

  const borderWidth = props.selectProps.noBorder ? '' : '!border'
  const borderColor = props.selectProps.error
    ? '!border-red-500'
    : props.selectProps.warning
      ? '!border-orange-200'
      : '!border-gray-300'

  const labelColor = props.selectProps.isDisabled ? 'text-gray-400' : 'text-gray-500'

  let labelText = props.selectProps.placeholder
  if (props.selectProps.required) labelText += ' *'
  const inputId = props.selectProps.inputId ? String(props.selectProps.inputId) : undefined

  return (
    <>
      <components.Control className={`w-full ${borderWidth} ${borderColor} flex flex-wrap relative`} {...props}>
        {children}
        {!!props.selectProps.placeholder && (
          inputId ? (
            <label htmlFor={inputId} className={`absolute max-w-full left-3 transition-all ${labelColor} ${labelCss}`}>
              <div className="relative top-[.4rem] truncate mr-0">{labelText}</div>
            </label>
          ) : (
            <div className={`absolute max-w-full left-3 transition-all ${labelColor} ${labelCss}`}>
              <div className="relative top-[.4rem] truncate mr-0">{labelText}</div>
            </div>
          )
        )}
      </components.Control>
      {!!props.selectProps.helper && (
        <div className="absolute -bottom-6 text-gray-400 text-sm mt-1 ml-1">{props.selectProps.helper}</div>
      )}
    </>
  )
}

const Input = (props: InputProps) => <components.Input id={props.id} inputClassName="text-gray-900" {...props} />

const Option = (props: OptionProps<Value> & CustomProps) => {
  const className = 'text-ellipsis overflow-hidden whitespace-nowrap'
  const optionProps = {
    ...props,
    innerProps: {
      ...props.innerProps,
      'aria-selected': false,
    },
  }

  if (props.selectProps?.tooltip) {
    return (
      <Tooltip title={props.label} zIndex={9999}>
        <span className={props.data?.draft === true ? 'text-gray-400 italic' : undefined} title={props.label}>
          <components.Option className={className} {...optionProps} />
        </span>
      </Tooltip>
    )
  }
  return (
    <span className={props.data?.draft === true ? 'text-gray-400 italic' : undefined} title={props.label}>
      <components.Option className={className} {...optionProps} />
    </span>
  )
}

const IndicatorSeparator = () => <div />
const Placeholder = () => <div />

const grey = 'hsl(0, 0%, 80%)'
export const control = (base: any, state: ControlProps & CustomProps) => ({
  ...base,
  ...(state.selectProps.noBorder ? { backgroundColor: 'transparent' } : {}),
  borderColor: grey,
  borderWidth: state.selectProps.noBorder ? '0 0 1px 0' : state.isFocused ? '2px' : '1px',
  boxShadow: state.isFocused ? 0 : 0,
  fontSize: '0.5rem',
  lineHeight: '1.25rem',
})

export const BaseSelect = React.forwardRef((props: BaseSelectProps, ref) => {
  const {
    styles: stylesProp,
    className,
    component,
    id,
    name,
    placeholder,
    error,
    insideModal,
    helper,
    outline = true,
    ariaLiveMessages: ariaLiveMessagesProp,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...rest
  } = props

  const { i18n } = useI18n()
  const Component = component
  const inputId = id ?? name
  const ariaLiveMessages = React.useMemo(
    () => ({
      ...ariaLiveMessagesProp,
      onChange: ({
        action,
        label,
        labels,
        isDisabled,
        ...changeProps
      }: AriaOnChangeProps<Value, boolean>) => {
        if (action === 'initial-input-focus') {
          const values = labels.filter(Boolean)

          return values.length ? `${values.join(', ')}.` : label ? `${label}.` : ''
        }

        if (ariaLiveMessagesProp?.onChange) {
          const onChangeProps = {
            action,
            label,
            labels,
            isDisabled,
            ...changeProps,
          } as AriaOnChangeProps<Value, boolean>

          return ariaLiveMessagesProp.onChange(onChangeProps)
        }

        switch (action) {
          case 'deselect-option':
          case 'pop-value':
          case 'remove-value':
            return `option ${label}, deselected.`
          case 'clear':
            return 'All selected options have been cleared.'
          case 'select-option':
            return isDisabled ? `option ${label} is disabled. Select another option.` : `option ${label}, selected.`
          default:
            return ''
        }
      },
      onFocus: ({
        context,
        label,
        isDisabled,
        focused,
        isAppleDevice,
        selectValue,
        ...focusProps
      }: AriaOnFocusProps<Value, GroupBase<Value>>) => {
        if (context === 'value') {
          const valueIndex = selectValue?.indexOf(focused)
          return valueIndex != null && valueIndex > -1
            ? `value ${label} focused, ${valueIndex + 1} of ${selectValue.length}.`
            : `value ${label} focused.`
        }

        if (isAppleDevice) {
          return `${label}${isDisabled ? ' disabled' : ''}.`
        }

        return ariaLiveMessagesProp?.onFocus?.({
          context,
          label,
          isDisabled,
          focused,
          isAppleDevice,
          selectValue,
          ...focusProps,
        }) ?? ''
      },
    }),
    [ariaLiveMessagesProp],
  )

  const modalProps = insideModal ? selectModalProps : { styles: {} }

  return (
    <div className={`relative flex w-full ${className || ''}`}>
      {/* @ts-ignore */}
      <Component
        {...modalProps}
        className="w-full"
        name={name}
        // Be sure that menu is on top of label (z-10)
        classNames={{
          menu: () => '!z-[11]',
          // Be sure that portal is on top everything inside modals
          menuPortal: () => '!z-[2000]',
        }}
        styles={{
          clearIndicator: (styles: React.CSSProperties) => ({
            ...styles,
            padding: 0,
          }),
          control,
          input: (styles: React.CSSProperties) => ({
            ...styles,
            fontSize: '1rem',
            lineHeight: '1.25rem',
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          multiValueLabel: (styles: React.CSSProperties) => ({
            ...styles,
            fontSize: '1rem',
            lineHeight: '1.25rem',
          }),
          option: (styles: React.CSSProperties) => ({
            ...styles,
            fontSize: '1rem',
            lineHeight: '1.25rem',
          }),
          // placeholder: (styles: React.CSSProperties) => ({
          //   ...styles,
          //   fontSize: '1rem',
          //   lineHeight: '1.25rem',
          // }),
          singleValue: (styles: React.CSSProperties) => ({
            ...styles,
            fontSize: '1rem',
            lineHeight: '1.25rem',
          }),
          ...stylesProp,
          ...(modalProps?.styles || {}),
        }}
        aria-label={ariaLabel ?? (!inputId && !ariaLabelledBy ? placeholder : undefined)}
        aria-labelledby={ariaLabelledBy}
        ariaLiveMessages={ariaLiveMessages}
        placeholder={props.noBorder ? null : placeholder || null}
        components={{
          ...(outline ? { Control } : {}),
          IndicatorSeparator,
          Input,
          Option,
          Placeholder,
        }}
        control={control}
        error={error}
        closeMenuOnSelect={!props.isMulti}
        loadingMessage={() => i18n('global_searching')}
        noOptionsMessage={() => i18n('global_no_options')}
        menuPlacement="auto"
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
        menuPortalTarget={document.querySelector('body')}
        helper={helper}
        ref={ref}
        inputId={inputId}
        {...rest}
      />
    </div>
  )
})
